import React, { useState, useEffect, useRef } from 'react'
import { FloppyDisk, Buildings, PaintBrush, UploadSimple, Image as ImagePhosphor, FileText, X, Trash, Moon, Sun } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useSettingsStore } from '@/stores/settingsStore'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import PhoneInput from '@/components/ui/PhoneInput'
import { Tooltip } from '@/components/ui/Tooltip'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { settingsApi, authApi, resolveFileUrl } from '@/lib/api'
import { useUnsavedChangesGuard } from '@/lib/useUnsavedChangesGuard'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { inputClass, inputStyle, labelClass, labelStyle, cardChromeClass, iconChipClass } from '@/lib/styleKit'

const AccountSection: React.FC = () => {
  const { config, fetchConfig } = useSettingsStore()
  const { actualTheme } = useThemeStore()
  const isDark = actualTheme === 'dark'

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [companyWebsite, setCompanyWebsite] = useState('')
  const [companyDescription, setCompanyDescription] = useState('')
  const [companyAddress, setCompanyAddress] = useState('')
  const [companyPhone, setCompanyPhone] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)
  const [savingCompany, setSavingCompany] = useState(false)

  // Baselines captured on load / after save — used to keep the Save buttons
  // disabled until the user actually edits a field.
  const [profileBaseline, setProfileBaseline] = useState({ fullName: '', email: '', username: '' })
  const [companyBaseline, setCompanyBaseline] = useState({
    companyName: '', companyWebsite: '', companyDescription: '', companyAddress: '', companyPhone: '',
  })

  const profileDirty =
    fullName !== profileBaseline.fullName ||
    email !== profileBaseline.email ||
    username !== profileBaseline.username

  const companyDirty =
    companyName !== companyBaseline.companyName ||
    companyWebsite !== companyBaseline.companyWebsite ||
    companyDescription !== companyBaseline.companyDescription ||
    companyAddress !== companyBaseline.companyAddress ||
    companyPhone !== companyBaseline.companyPhone

  useUnsavedChangesGuard(profileDirty || companyDirty)

  // Branding state
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [brandBookUrl, setBrandBookUrl] = useState<string | null>(null)
  const [brandBookName, setBrandBookName] = useState('')
  const [brandColors, setBrandColors] = useState<{ primary: string; secondary: string; accent: string }>({ primary: '#1E40AF', secondary: '#0F172A', accent: '#F97316' })
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingBook, setUploadingBook] = useState(false)
  const [savingColors, setSavingColors] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const bookInputRef = useRef<HTMLInputElement>(null)

  // Both branding deletes are irreversible server-side, so each is gated by a
  // confirmation and its own in-flight flag (the flag is what `loading` binds
  // to, so the dialog's confirm button cannot be fired twice).
  const [confirmingLogoDelete, setConfirmingLogoDelete] = useState(false)
  const [deletingLogo, setDeletingLogo] = useState(false)
  const [confirmingBookDelete, setConfirmingBookDelete] = useState(false)
  const [deletingBook, setDeletingBook] = useState(false)
  // Spoken outcome for the two async deletes — a toast is visual only.
  const [brandingStatus, setBrandingStatus] = useState('')

  useEffect(() => {
    authApi.getMe().then(res => {
      const fn = res.data.full_name || ''
      const em = res.data.email || ''
      const un = res.data.username || ''
      setFullName(fn)
      setEmail(em)
      setUsername(un)
      setProfileBaseline({ fullName: fn, email: em, username: un })
    })
    authApi.getAccountSettings().then(res => {
      const addr = res.data.company_address || ''
      const phone = res.data.company_phone || ''
      setCompanyAddress(addr)
      setCompanyPhone(phone)
      setCompanyBaseline(prev => ({ ...prev, companyAddress: addr, companyPhone: phone }))
      if (res.data.company_logo_url) setLogoUrl(res.data.company_logo_url)
      if (res.data.brand_book_url) {
        setBrandBookUrl(res.data.brand_book_url)
        setBrandBookName(res.data.brand_book_url.split('/').pop() || 'brand-book')
      }
      if (res.data.brand_colors) {
        setBrandColors(prev => ({ ...prev, ...res.data.brand_colors }))
      }
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (config) {
      const name = config.company_name || ''
      const website = config.company_website || ''
      const description = config.company_description || ''
      setCompanyName(name)
      setCompanyWebsite(website)
      setCompanyDescription(description)
      setCompanyBaseline(prev => ({ ...prev, companyName: name, companyWebsite: website, companyDescription: description }))
    }
  }, [config])

  const handleSaveProfile = async () => {
    const trimmedUsername = username.trim()
    if (!trimmedUsername) {
      toast.error('Username cannot be empty')
      return
    }
    setSavingProfile(true)
    try {
      const res = await settingsApi.updateProfile({ full_name: fullName, email, username: trimmedUsername })
      // Reflect the canonical saved values (e.g. a trimmed username) back into the fields.
      const savedFullName = res.data.full_name ?? fullName
      const savedEmail = res.data.email ?? email
      const savedUsername = res.data.username ?? trimmedUsername
      setFullName(savedFullName)
      setEmail(savedEmail)
      setUsername(savedUsername)
      setProfileBaseline({ fullName: savedFullName, email: savedEmail, username: savedUsername })
      // The signed-in user lives in the auth store, and the sidebar renders the
      // name and initials straight off it. Saving only updated these local
      // fields, so the old name stayed in the sidebar until a hard refresh
      // re-fetched /auth/me -- the row said one thing and the form another.
      //
      // PUT /auth/me answers with the whole UserResponse, so this is the
      // canonical record, not a guess assembled from what was typed. Merged
      // over the user already held rather than replacing it, so a field the
      // response happens not to carry cannot be dropped on the way through.
      // getState() rather than a subscription: this runs in an async handler
      // after an await, where a captured value could already be stale.
      const current = useAuthStore.getState().user
      if (current) {
        useAuthStore.getState().updateUser({ ...current, ...(res.data ?? {}) })
      }
      toast.success('Profile updated')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to update profile')
    } finally {
      setSavingProfile(false)
    }
  }

  const handleSaveCompany = async () => {
    setSavingCompany(true)
    try {
      await settingsApi.updateConfig({
        company_name: companyName,
        company_website: companyWebsite,
        company_description: companyDescription,
      })
      await authApi.updateAccountSettings({
        company_name: companyName,
        company_website: companyWebsite,
        company_address: companyAddress,
        company_phone: companyPhone,
      })
      await fetchConfig()
      setCompanyBaseline({ companyName, companyWebsite, companyDescription, companyAddress, companyPhone })
      toast.success('Company info saved')
    } catch {
      toast.error('Failed to save company info')
    } finally {
      setSavingCompany(false)
    }
  }

  const handleDeleteLogo = async () => {
    setDeletingLogo(true)
    setBrandingStatus('Removing company logo…')
    try {
      await authApi.deleteCompanyLogo()
      setLogoUrl(null)
      setBrandingStatus('Company logo removed')
      toast.success('Logo removed')
    } catch {
      setBrandingStatus('Could not remove the company logo')
      toast.error('Failed to remove logo')
    } finally {
      setDeletingLogo(false)
      setConfirmingLogoDelete(false)
    }
  }

  const handleDeleteBrandBook = async () => {
    setDeletingBook(true)
    setBrandingStatus('Removing brand book…')
    try {
      await authApi.deleteBrandBook()
      setBrandBookUrl(null)
      setBrandBookName('')
      setBrandingStatus('Brand book removed')
      toast.success('Brand book removed')
    } catch {
      setBrandingStatus('Could not remove the brand book')
      toast.error('Failed to remove')
    } finally {
      setDeletingBook(false)
      setConfirmingBookDelete(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Account & Profile
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          Manage your personal information and company details.
        </p>
      </div>

      <Card className={cardChromeClass}>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>Your basic account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label htmlFor="account-full-name" className={labelClass} style={labelStyle}>Full Name</label>
            <input id="account-full-name" type="text" value={fullName} onChange={e => setFullName(e.target.value)} className={inputClass} style={inputStyle} />
          </div>
          <div>
            <label htmlFor="account-email" className={labelClass} style={labelStyle}>Email</label>
            <input id="account-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} style={inputStyle} />
          </div>
          <div>
            <label htmlFor="account-username" className={labelClass} style={labelStyle}>Username</label>
            <input id="account-username" type="text" value={username} onChange={e => setUsername(e.target.value)} className={inputClass} style={inputStyle} />
            <p className="text-muted-foreground dark:text-[#62666d] mt-1" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>Used to sign in. Must be unique.</p>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleSaveProfile}
            loading={savingProfile}
            disabled={!profileDirty}
            icon={<FloppyDisk className="w-4 h-4" weight="bold" />}
          >
            Save Profile
          </Button>
        </CardContent>
      </Card>

      <Card className={cardChromeClass}>
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className={iconChipClass}>
              <Buildings className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            </div>
            <div>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Used for AI-generated content and template variables</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label htmlFor="account-company-name" className={labelClass} style={labelStyle}>Company Name</label>
            <input id="account-company-name" type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Inc." className={inputClass} style={inputStyle} />
          </div>
          <div>
            <label htmlFor="account-company-website" className={labelClass} style={labelStyle}>Website</label>
            <input id="account-company-website" type="url" value={companyWebsite} onChange={e => setCompanyWebsite(e.target.value)} placeholder="https://example.com" className={inputClass} style={inputStyle} />
          </div>
          <div>
            <label htmlFor="account-company-description" className={labelClass} style={labelStyle}>Description</label>
            <textarea
              id="account-company-description"
              value={companyDescription}
              onChange={e => setCompanyDescription(e.target.value)}
              placeholder="Briefly describe what your company does, its value proposition, and target market..."
              rows={4}
              className={cn(inputClass, 'h-[96px] py-3 resize-none overflow-y-auto leading-relaxed')}
              style={inputStyle}
            />
          </div>
          <div>
            <label htmlFor="account-company-address" className={labelClass} style={labelStyle}>Address</label>
            <input id="account-company-address" type="text" value={companyAddress} onChange={e => setCompanyAddress(e.target.value)} placeholder="123 Main St, City, State 12345" className={inputClass} style={inputStyle} />
          </div>
          <div data-settings-anchor="account-phone">
            {/* PhoneInput owns no bindable id (§56.1) — the field names itself via `ariaLabel`. */}
            <label className={labelClass} style={labelStyle}>Phone</label>
            <PhoneInput value={companyPhone} onChange={setCompanyPhone} placeholder="(555) 123-4567" ariaLabel="Phone" />
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleSaveCompany}
            loading={savingCompany}
            disabled={!companyDirty}
            icon={<FloppyDisk className="w-4 h-4" weight="bold" />}
          >
            Save Company Info
          </Button>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card className={cardChromeClass} data-settings-anchor="account-appearance">
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className={iconChipClass}>
              {isDark
                ? <Moon className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                : <Sun className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />}
            </div>
            <div>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Switch between light and dark mode</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>Dark mode</p>
              <p className="text-muted-foreground dark:text-[#8a8f98] mt-0.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                {isDark ? 'Dark theme is on' : 'Light theme is on'}
              </p>
            </div>
            <ThemeToggle />
          </div>
        </CardContent>
      </Card>

      {/* Branding */}
      <Card className={cardChromeClass}>
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className={iconChipClass}>
              <PaintBrush className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            </div>
            <div>
              <CardTitle>Branding</CardTitle>
              <CardDescription>Logo, colors, and brand guidelines used by AI when generating content</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo Upload */}
          <div data-settings-anchor="account-logo">
            <label className={labelClass} style={labelStyle}>Company Logo</label>
            <div className="flex items-start gap-4">
              {logoUrl ? (
                <div className="relative">
                  <div className="w-24 h-24 rounded-md border border-border/50 dark:border-white/[0.08] bg-background dark:bg-white/[0.02] flex items-center justify-center overflow-hidden">
                    <img src={resolveFileUrl(logoUrl)} alt="Company logo" className="max-w-full max-h-full object-contain p-2" />
                  </div>
                  {/* Permanently visible, not hover-revealed: a hover-only
                      destructive control is unreachable by touch and invisible
                      to a keyboard user. Quiet by default, red on hover/focus. */}
                  <button
                    type="button"
                    onClick={() => setConfirmingLogoDelete(true)}
                    disabled={deletingLogo}
                    aria-busy={deletingLogo || undefined}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full border border-border/60 dark:border-white/[0.10] bg-card dark:bg-[#191a1b] text-muted-foreground dark:text-[#8a8f98] hover:text-red-600 dark:hover:text-red-400 hover:border-red-500/40 dark:hover:border-red-500/40 focus-visible:outline-none focus-visible:border-[#7170ff]/70 focus-visible:text-red-600 dark:focus-visible:text-red-400 disabled:opacity-50 flex items-center justify-center shadow-sm transition-colors duration-150"
                    aria-label="Remove company logo"
                  >
                    <X className="w-3 h-3" weight="bold" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="w-24 h-24 rounded-md border-2 border-dashed border-border/60 hover:border-[#5e6ad2]/60 dark:border-white/[0.08] dark:hover:border-[#5e6ad2]/50 flex flex-col items-center justify-center gap-1 transition-colors duration-150 cursor-pointer hover:bg-muted/40 dark:hover:bg-white/[0.03]"
                >
                  {uploadingLogo ? (
                    <div className="w-5 h-5 border-2 border-[#5e6ad2] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <ImagePhosphor className="w-5 h-5 text-muted-foreground/60 dark:text-[#62666d]" />
                      <span className="text-muted-foreground/70 dark:text-[#62666d] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>Upload</span>
                    </>
                  )}
                </button>
              )}
              <div className="flex-1 pt-1">
                <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>PNG, JPG, SVG, or WebP. Used in AI-generated presentations and emails.</p>
                {logoUrl && (
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline mt-1"
                    style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    Replace logo
                  </button>
                )}
              </div>
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg,.webp"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                setUploadingLogo(true)
                try {
                  const res = await authApi.uploadCompanyLogo(file)
                  setLogoUrl(res.data.logo_url)
                  toast.success('Logo uploaded')
                } catch { toast.error('Failed to upload logo') }
                finally { setUploadingLogo(false); e.target.value = '' }
              }}
            />
          </div>

          {/* Brand Colors */}
          <div data-settings-anchor="account-brand-colors">
            <label className={labelClass} style={labelStyle}>Brand Colors</label>
            <div className="grid grid-cols-3 gap-3">
              {(['primary', 'secondary', 'accent'] as const).map(key => (
                <div key={key} className="space-y-1.5">
                  <span className="text-muted-foreground dark:text-[#62666d] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>{key}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={brandColors[key]}
                      onChange={e => setBrandColors(prev => ({ ...prev, [key]: e.target.value }))}
                      className="w-9 h-9 rounded-md border border-border/50 dark:border-white/[0.08] cursor-pointer p-0.5 bg-background dark:bg-white/[0.02]"
                      aria-label={`${key} color`}
                    />
                    <input
                      type="text"
                      value={brandColors[key]}
                      onChange={e => setBrandColors(prev => ({ ...prev, [key]: e.target.value }))}
                      className="flex-1 h-9 px-3 font-mono bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] rounded-md text-foreground dark:text-[#f7f8f8] focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 outline-none transition-colors duration-150"
                      placeholder="#000000"
                      style={{ fontSize: '12px', fontWeight: 510 }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              loading={savingColors}
              icon={<FloppyDisk className="w-3.5 h-3.5" weight="bold" />}
              onClick={async () => {
                setSavingColors(true)
                try {
                  await authApi.updateAccountSettings({ brand_colors: brandColors })
                  toast.success('Brand colors saved')
                } catch { toast.error('Failed to save colors') }
                finally { setSavingColors(false) }
              }}
            >
              Save Colors
            </Button>
          </div>

          {/* Brand Book Upload */}
          <div data-settings-anchor="account-brand-book">
            <label className={labelClass} style={labelStyle}>Brand Book / Guidelines</label>
            {brandBookUrl ? (
              <div className="flex items-center gap-3 rounded-md border border-border/50 dark:border-white/[0.08] bg-muted/40 dark:bg-white/[0.02] px-3.5 py-3">
                <div className="w-9 h-9 rounded-md bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{brandBookName}</p>
                  <p className="text-muted-foreground dark:text-[#62666d]" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>Brand guidelines document</p>
                </div>
                <div className="flex items-center gap-1">
                  <Tooltip label="Replace">
                    <button
                      type="button"
                      onClick={() => bookInputRef.current?.click()}
                      className="p-1.5 text-muted-foreground dark:text-[#8a8f98] hover:text-[#5e6ad2] dark:hover:text-[#a5b4fc] rounded-md hover:bg-muted/60 dark:hover:bg-white/[0.04] transition-colors duration-150"
                      aria-label="Replace brand book"
                    >
                      <UploadSimple className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                  <Tooltip label="Remove">
                    <button
                      type="button"
                      onClick={() => setConfirmingBookDelete(true)}
                      disabled={deletingBook}
                      aria-busy={deletingBook || undefined}
                      className="p-1.5 text-muted-foreground dark:text-[#8a8f98] hover:text-red-600 dark:hover:text-red-400 rounded-md hover:bg-red-500/[0.08] dark:hover:bg-red-500/[0.10] disabled:opacity-50 transition-colors duration-150"
                      aria-label="Remove brand book"
                    >
                      <Trash className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => bookInputRef.current?.click()}
                disabled={uploadingBook}
                className="w-full flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-border/60 hover:border-[#5e6ad2]/60 dark:border-white/[0.08] dark:hover:border-[#5e6ad2]/50 py-5 transition-colors duration-150 cursor-pointer hover:bg-muted/40 dark:hover:bg-white/[0.03]"
              >
                {uploadingBook ? (
                  <div className="w-5 h-5 border-2 border-[#5e6ad2] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <UploadSimple className="w-4 h-4 text-muted-foreground/60 dark:text-[#62666d]" />
                    <span className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>Upload brand book (PDF, DOCX, PPTX)</span>
                  </>
                )}
              </button>
            )}
            <input
              ref={bookInputRef}
              type="file"
              accept=".pdf,.docx,.pptx,.doc,.ppt"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                setUploadingBook(true)
                try {
                  const res = await authApi.uploadBrandBook(file)
                  setBrandBookUrl(res.data.brand_book_url)
                  setBrandBookName(file.name)
                  toast.success('Brand book uploaded')
                } catch { toast.error('Failed to upload brand book') }
                finally { setUploadingBook(false); e.target.value = '' }
              }}
            />
          </div>

          {/* Spoken outcome for the two deletes above — a toast is visual only.
              Last child on purpose: `sr-only` is absolutely positioned, so the
              `space-y-6` margin it inherits here costs no layout, whereas as the
              first child it would push the whole card body down. */}
          <p role="status" aria-live="polite" className="sr-only">{brandingStatus}</p>
        </CardContent>
      </Card>

      {/* Both deletes are server-side and irreversible — the file is gone and
          can only come back by uploading it again. */}
      <ConfirmDialog
        open={confirmingLogoDelete}
        title="Remove company logo?"
        description="Your logo is deleted from your account and stops appearing in AI-generated presentations and emails. You can upload it again at any time."
        confirmLabel="Remove logo"
        tone="destructive"
        loading={deletingLogo}
        onConfirm={handleDeleteLogo}
        onCancel={() => setConfirmingLogoDelete(false)}
      />
      <ConfirmDialog
        open={confirmingBookDelete}
        title="Remove brand book?"
        description={`${brandBookName || 'The brand book'} is deleted from your account and AI stops using it as brand guidance when generating content. You can upload it again at any time.`}
        confirmLabel="Remove brand book"
        tone="destructive"
        loading={deletingBook}
        onConfirm={handleDeleteBrandBook}
        onCancel={() => setConfirmingBookDelete(false)}
      />
    </div>
  )
}

export default AccountSection
